import type { Change, EntityKind } from "@hq/contracts";
import type { LocalStore } from "@hq/local-store";

type OwnRevisions = { from: number; to: number };
const key = (kind: EntityKind, id: string) => `sync:own-revisions:${kind}:${id}`;

/** Keep only the latest uninterrupted run of our own server revisions. Remote writes
 * break the chain: drafts from before them must still conflict. Called inside the pull
 * transaction, including when there are no queued mutations but an editor is dirty. */
export async function recordOwnRevision(tx: LocalStore, change: Change, deviceId: string) {
  if (change.entity !== "note" && change.entity !== "task") return;
  const entry = key(change.entity, change.id);
  if (change.deviceId !== deviceId || change.conflicts?.length) {
    await tx.kv.delete(entry);
    return;
  }
  const previous = await tx.kv.get<OwnRevisions>(entry);
  const to = change.row.revision;
  await tx.kv.set(entry, {
    from: previous?.to === to - 1 ? previous.from : to - 1,
    to,
  } satisfies OwnRevisions);
}

/** Translate a captured draft base only across acknowledgments of our own writes.
 * Read in the same transaction as the save so a concurrent pull cannot race this check. */
export async function rebaseDraftRevision(
  tx: LocalStore,
  kind: EntityKind,
  id: string,
  base: number,
  current: number,
): Promise<number> {
  const own = await tx.kv.get<OwnRevisions>(key(kind, id));
  return own && own.to === current && base >= own.from && base <= own.to ? own.to : base;
}
